import { SettingOutlined } from '@ant-design/icons';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { App, Col, Dropdown, type MenuProps, Row } from 'antd';
import React from 'react';

import { useSignOutMutation } from '@/apis/user.api.ts';
import { useLang } from '@/i18n/use-lang.ts';
import { Authentication } from '@/rbac';
import { useNavMenus } from '@/routes/-use-nav-menus.tsx';
import Logo from '@/routes/auth/-components/Logo.tsx';
import { useSupervisorStats } from '@/routes/dashboard/-hooks';
import {
  CallsVolumeChart,
  HandledRejectedRate,
  InboundCallStatistics,
  KpiCards,
  OperatorStatusPanel,
} from '@/routes/dashboard/-statistics';
import { useUserStore } from '@/stores/user.store.ts';
import { cn } from '@/utils/cn';

import DateTimeDisplay from './-components/DateTimeDisplay';
import OperatorOverview from './-components/OperatorOverview';
import './-styles.css';

const dashboardLangs = {
  'en-GB': {
    kpi_totalCallsToday: 'Total Calls\ntoday',
    kpi_totalCallsLastHour: 'Total calls last\nhour',
    kpi_callsInProgress: 'Number of calls\nin progress',
    kpi_avgResponseTime: 'Average\nResponse Time',
    kpi_liveGlobalQueueLength: 'Live global\nqueue length',
    inbound_answerTime60s: 'Answer-time\n≤ 60s',
    inbound_answerTime180s: 'Answer-time\n≤ 180s',
    inbound_missedRateToday: 'Missed Rate\nToday',
  },
  'zh-CN': {
    kpi_totalCallsToday: '今日总通话',
    kpi_totalCallsLastHour: '过去一小时\n总通话',
    kpi_callsInProgress: '进行中通话数',
    kpi_avgResponseTime: '平均\n响应时间',
    kpi_liveGlobalQueueLength: '全局队列\n实时长度',
    inbound_answerTime60s: '60秒内\n接听率',
    inbound_answerTime180s: '180秒内\n接听率',
    inbound_missedRateToday: '今日\n未接率',
  },
  'cy-GB': {
    kpi_totalCallsToday: 'Cyfanswm galwadau\nheddiw',
    kpi_totalCallsLastHour: 'Cyfanswm galwadau\nyr awr ddiwethaf',
    kpi_callsInProgress: 'Galwadau ar y gweill',
    kpi_avgResponseTime: 'Amser ymateb\ncymedrig',
    kpi_liveGlobalQueueLength: 'Hyd ciw byw',
    inbound_answerTime60s: 'Amser ateb ≤60s',
    inbound_answerTime180s: 'Amser ateb ≤180s',
    inbound_missedRateToday: 'Cyfradd methu heddiw',
  },
};

const DASHBOARD_VARS = {
  '--stats-card-bg':
    'linear-gradient(100.44deg, #080c2b 4.74%, #1b2260 94.59%), linear-gradient(0deg, #252736, #252736) #252736',
  '--stats-text': '#ffffff',
  '--stats-text-secondary': '#94a3b8',
  '--stats-bar-track': 'rgba(255, 255, 255, 0.08)',
} as React.CSSProperties;

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
  beforeLoad: async () => {
    // check user auth
    const user = useUserStore.getState().user;
    if (!user?.accessToken) {
      return redirect({
        to: '/auth/sign-in',
        search: { redirect: '/dashboard' },
      });
    }
  },
});

function DashboardPage() {
  const l = useLang(dashboardLangs);
  const { metrics, operators, kpiItems, operatorStatuses, inboundStatsItems } =
    useSupervisorStats({
      operatorSort: false,
    });
  const { message } = App.useApp();
  const signOutMutation = useSignOutMutation();

  const navMenus = useNavMenus([], 'wallboard');

  const menuItems: MenuProps['items'] = [
    ...navMenus,
    {
      key: 'sign-out',
      label: l('signOut'),
      danger: true,
      onClick: () => {
        signOutMutation.mutateAsync().catch(() => {
          message.error(l('signOutFailed'));
        });
      },
    },
  ];

  const kpiItemsWithLabels = React.useMemo(
    () =>
      kpiItems.map((i) => ({
        ...i,
        label: l(i.labelKey as keyof (typeof dashboardLangs)['en-GB']),
      })),
    [kpiItems, l],
  );
  const inboundStatsItemsWithLabels = React.useMemo(
    () =>
      inboundStatsItems.map((i) => ({
        ...i,
        label: l(i.labelKey as keyof (typeof dashboardLangs)['en-GB']),
      })),
    [inboundStatsItems, l],
  );

  return (
    <Authentication require={'arc:wallboard:*'} failMode={'empty'}>
      <div
        style={DASHBOARD_VARS}
        className={cn(
          'dashboard flex h-dvh w-screen flex-col overflow-auto p-4 2xl:p-6',
          'bg-[radial-gradient(59.44%_95.11%_at_50%_58.78%,rgba(22,46,206,0.4)_26.08%,rgba(25,0,83,0.4)_69.78%,rgba(30,20,55,0.4)_100%),linear-gradient(0deg,#31365e,#31365e)]',
        )}
      >
        <div className="mb-4 flex items-center justify-between">
          <Logo />
          <div className={'flex items-center gap-8'}>
            <DateTimeDisplay />
            <Dropdown menu={{ items: menuItems }}>
              <SettingOutlined className="cursor-pointer text-white!" />
            </Dropdown>
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          <Row gutter={20}>
            <Col span={16}>
              <KpiCards items={kpiItemsWithLabels} />
            </Col>
            <Col span={8}>
              <HandledRejectedRate
                handledRate={metrics?.today.rates.handled_rate ?? 0}
                rejectedRate={metrics?.today.rates.rejected_rate ?? 0}
              />
            </Col>
          </Row>
          <div className={'h-5'} />
          <Row gutter={20} className="flex-1">
            <Col span={16}>
              <OperatorOverview operators={operators?.operators ?? []} />
            </Col>
            <Col span={8}>
              <OperatorStatusPanel statuses={operatorStatuses} />
            </Col>
          </Row>
          <div className={'h-5'} />
          <Row gutter={20} className="flex-1">
            <Col span={16}>
              <CallsVolumeChart
                inbound={metrics?.volumes.inbound ?? []}
                outbound={metrics?.volumes.outbound ?? []}
                dark
                fontSize={20}
              />
            </Col>
            <Col span={8}>
              <InboundCallStatistics items={inboundStatsItemsWithLabels} />
            </Col>
          </Row>
        </div>
      </div>
    </Authentication>
  );
}
